/* =========================================================
   Icon columns — entrada única de la sección

   Encabezado, párrafo y cuadrícula entran juntos como un solo bloque:
   opacity 0 -> 1 + translateY 24px -> 0, 0.7s, power2.out. Se dispara una vez
   (once: true) cuando la sección llega al 85% del viewport. Nada más.

   El contenido nunca depende de GSAP para verse:
     - El estado oculto lo pone este JS (clase .is-awaiting-reveal), no el CSS
       base. Sin JS, la sección se ve directamente.
     - Si GSAP falla o tarda más de REVEAL_TIMEOUT, se quita la clase y la
       sección se queda visible sin animar.
     - Con prefers-reduced-motion: reduce no se oculta nada.
     - Al re-renderizar en el Theme Editor (shopify:section:load) se muestra ya
       visible: el scroll que la dispararía puede no llegar a ocurrir.
   ========================================================= */

(function () {
  'use strict';

  /* En el Theme Editor la sección re-inyecta su <script> y este archivo se
     vuelve a ejecutar. Si ya hay un módulo vivo, reutilizamos el suyo. */
  if (window.DermxIconColumns) {
    window.DermxIconColumns.initAll();
    return;
  }

  var GSAP_VERSION = '3.13.0';
  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/gsap.min.js';
  var SCROLLTRIGGER_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/ScrollTrigger.min.js';

  var REVEAL_TIMEOUT = 3000;
  var PENDING_CLASS = 'is-awaiting-reveal';

  /* Una instancia por section.id: { tween, timer, revealed } */
  var instances = new Map();

  /* ---------------------------------------------------------
     Carga de GSAP + ScrollTrigger (una sola vez por página)
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> ScrollTrigger
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  /* Promesa compartida con el resto de secciones GSAP del tema: la primera
     que llegue monta GSAP y las demás se enganchan a la misma promesa. */
  function ensureGsap() {
    if (window.dermxGsapReady) return window.dermxGsapReady;

    window.dermxGsapReady = Promise.resolve()
      .then(function () {
        if (!window.gsap) return loadScript(GSAP_URL);
        return null;
      })
      .then(function () {
        if (!window.gsap) throw new Error('[icon-columns] GSAP no disponible');
        if (!window.ScrollTrigger && !isPluginRegistered('ScrollTrigger')) {
          return loadScript(SCROLLTRIGGER_URL);
        }
        return null;
      })
      .then(function () {
        var gsap = window.gsap;
        var ScrollTrigger = window.ScrollTrigger || gsap.core.globals().ScrollTrigger;

        if (!ScrollTrigger) throw new Error('[icon-columns] ScrollTrigger no disponible');
        if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

        return { gsap: gsap, ScrollTrigger: ScrollTrigger };
      });

    return window.dermxGsapReady;
  }

  /* ---------------------------------------------------------
     Ciclo de vida por sección
     --------------------------------------------------------- */

  function getContent(root) {
    return root.querySelector('[data-icon-columns-content]');
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function showNow(root) {
    var content = getContent(root);
    if (!content) return;
    content.classList.remove(PENDING_CLASS);
    if (window.gsap) window.gsap.set(content, { clearProps: 'opacity,transform' });
  }

  /* Muestra la sección sin animar y la deja registrada como revelada, para que
     una re-ejecución de este script (initAll) no la vuelva a ocultar. */
  function reveal(root) {
    var sectionId = root.dataset.sectionId;
    destroy(sectionId);
    instances.set(sectionId, { tween: null, timer: null, revealed: true });
    showNow(root);
  }

  function destroy(sectionId) {
    var instance = instances.get(sectionId);
    if (!instance) return;

    clearTimeout(instance.timer);
    if (instance.tween) {
      if (instance.tween.scrollTrigger) instance.tween.scrollTrigger.kill();
      instance.tween.kill();
    }
    instance.revealed = true; // una promesa de GSAP pendiente ya no debe montar nada
    instances.delete(sectionId);
  }

  function animate(root, instance, gsap) {
    var content = getContent(root);
    if (!content) return;

    // gsap.set fija el estado inicial en línea antes de soltar la clase:
    // el relevo clase -> estilos inline no produce parpadeo.
    gsap.set(content, { opacity: 0, y: 24 });
    content.classList.remove(PENDING_CLASS);

    instance.tween = gsap.to(content, {
      opacity: 1,
      y: 0,
      duration: 0.7,
      ease: 'power2.out',
      clearProps: 'opacity,transform',
      scrollTrigger: {
        trigger: root,
        start: 'top 85%',
        once: true
      }
    });
  }

  /* options.immediate: mostrar sin animar (re-render en el Theme Editor). */
  function init(root, options) {
    if (!root) return;

    var sectionId = root.dataset.sectionId;
    var content = getContent(root);
    if (!sectionId || !content) return;

    destroy(sectionId); // evita duplicados al re-renderizar en el editor

    if ((options && options.immediate) || prefersReducedMotion()) {
      reveal(root);
      return;
    }

    var instance = { tween: null, timer: null, revealed: false };
    instances.set(sectionId, instance);

    content.classList.add(PENDING_CLASS);

    // Red de seguridad: si GSAP tarda demasiado, se enseña sin animación.
    instance.timer = setTimeout(function () {
      instance.revealed = true;
      showNow(root);
    }, REVEAL_TIMEOUT);

    ensureGsap()
      .then(function (lib) {
        clearTimeout(instance.timer);
        // El editor puede haber quitado la sección, o el timeout ya la mostró.
        if (instance.revealed || !root.isConnected) return;
        animate(root, instance, lib.gsap);
      })
      .catch(function (error) {
        clearTimeout(instance.timer);
        showNow(root);
        console.warn(error);
      });
  }

  function initAll() {
    document.querySelectorAll('[data-icon-columns]').forEach(function (root) {
      // En una re-ejecución del script no se reinician las secciones vivas.
      if (instances.has(root.dataset.sectionId)) return;
      init(root);
    });
  }

  function findRoot(event) {
    return event.target.querySelector('[data-icon-columns]');
  }

  window.DermxIconColumns = { init: init, initAll: initAll, destroy: destroy, reveal: reveal };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ---------------------------------------------------------
     Theme Editor
     --------------------------------------------------------- */

  document.addEventListener('shopify:section:load', function (event) {
    var root = findRoot(event);
    if (root) init(root, { immediate: true });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = findRoot(event);
    if (root) destroy(root.dataset.sectionId);
  });

  /* Seleccionar un bloque en el editor debe enseñarlo aunque la sección aún
     no haya llegado a su punto de disparo. */
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest('[data-icon-columns]');
    if (root) reveal(root);
  });
})();
